import Vue from 'vue'
import VueRouter from 'vue-router'

import {
    userStore,
    UserService,
    ApplicantService,
    authenticationStore,
    AuthenticationService
} from '@/store'

import ID_CARD_ROUTES from '@/id-card/router'
import CITIZEN_PASSPORT_ROUTES from '@/citizen-passport/router'
import COMBO_APPLICATION_ROUTES from '@/combo-application/router'

import Home from '@/commons/views/Home.vue'
import Login from '@/commons/views/Login.vue'
import Success from '@/commons/components/payment/Success.vue'
import { ErrorUtils } from '@/commons/utils/error'
import { ApplicationRoute, SubApplicationRoute } from '@/commons/types'
import { RequestAuthenticationError, GeneralError } from '@/store/entities'
import AccessibilityNotice from '@/commons/components/accessibility/AccessibilityNotice.vue'
import AccessibilityManual from '@/commons/components/accessibility/AccessibilityManual.vue'

Vue.use(VueRouter)

function requestPaymentErrorHandler (titleTranslationKey = 'errors.payment.title') {
    ErrorUtils.requestErrorHandler(new GeneralError([
        'errors.payment.message'
    ], titleTranslationKey))
}

const PAYMENT_ROUTES: Array<SubApplicationRoute> = [
    {
        path: '/payment/success/:applicationType',
        props: true,
        component: Success,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/payment/cancel',
        meta: {
            requiresAuth: false
        },
        redirect: () => {
            requestPaymentErrorHandler('errors.payment.cancelled.title')
            return '/'
        }
    },
    {
        path: '/payment/error',
        meta: {
            requiresAuth: false
        },
        redirect: () => {
            requestPaymentErrorHandler()
            return '/'
        }
    }
]

const GENERAL_ROUTES: Array<SubApplicationRoute> = [
    {
        path: '/accessibility',
        props: true,
        component: AccessibilityNotice,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/accessibility-manual',
        props: true,
        component: AccessibilityManual,
        meta: {
            requiresAuth: false
        }
    }
]

const routes: Array<ApplicationRoute> = [
    ...ID_CARD_ROUTES,
    ...CITIZEN_PASSPORT_ROUTES,
    ...COMBO_APPLICATION_ROUTES,
    ...PAYMENT_ROUTES,
    ...GENERAL_ROUTES,
    {
        path: '/login/return/success/:authenticationToken',
        name: 'loginAuthorize',
        beforeEnter: (to, from, next) => {
            const pathParams = to.params as { authenticationToken: string }
            AuthenticationService.login(pathParams.authenticationToken)
                .then(() => next('/'))
                .catch(() => {
                    ErrorUtils.requestErrorHandler(new RequestAuthenticationError([
                        'errors.request.authentication.loginFailed'
                    ]))
                })
        },
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/login',
        name: 'login',
        component: Login,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '*',
        name: 'home',
        component: Home,
        meta: {
            requiresAuth: true
        }
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior () {
        return { x: 0, y: 0 }
    }
})

router.beforeEach((to, from, next) => {
    try {
        UserService.cancelUserRrRequest()
        if (authenticationStore.isValidUserCredentials()) {
            userStore.setUserInfo(authenticationStore.getUserCredentials())
            next(to.name === 'login' || to.name === 'loginAuthorize' ? '/' : undefined)
        } else {
            next(to.matched.some(record => record.meta.requiresAuth) ? '/login' : undefined)
        }

        if (!to.meta?.keepApplicationStore) {
            ApplicantService.resetApplicantStore()
        }
    } catch (e) {
        ErrorUtils.routerErrorHandler(e)
    }
})

export default router
